import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CompanyUser } from "@/apis/gql/types";
import { CommentInputField } from "@/components/shared/form/comment-input";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/spinner";
import { useTenant } from "@/providers/session";
import { useCreateInvoiceComment } from "@/queries/invoice-comments/create";

interface CommentInputProps {
  invoiceId: string;
  onCommentAdded?: () => void;
}

interface CommentFormValues {
  comment: string;
}

export const CommentInput = ({
  invoiceId,
  onCommentAdded,
}: CommentInputProps) => {
  const { t } = useTranslation();
  const { companyUsers } = useTenant();

  const form = useForm<CommentFormValues>({
    defaultValues: {
      comment: "",
    },
  });
  const [createComment, { loading }] = useCreateInvoiceComment();

  const onSubmit = async (data: CommentFormValues) => {
    if (!data.comment.trim()) return;

    form.reset();

    await createComment(
      {
        invoiceId,
        comment: data.comment.trim(),
      },
      () => {
        onCommentAdded?.();
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const _form = e.currentTarget.closest("form");
      _form?.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true }),
      );
    }
  };

  const users =
    companyUsers?.map((user: CompanyUser) => ({
      id: user.userId,
      display: user.name,
    })) ?? [];

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2 bg-background"
      >
        <CommentInputField
          users={users}
          onKeyDown={handleKeyDown}
          name="comment"
          forceSuggestionsAboveCursor
        />
        <div className="flex justify-end">
          {form.watch("comment")?.trim() && !loading && (
            <Button type="submit" size="sm" variant="outline">
              {loading && <LoadingSpinner />}
              {t("invoice_comments.add")}
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
